import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../../components/Navbar';
import SidebarSeccion from '../../components/SidebarSeccion';
import { useAuth, API_URL } from '../../context/AuthContext';
import { getTema } from '../../theme';
import { PencilSquareIcon, TrashIcon, PlayIcon } from '@heroicons/react/24/outline';

const CATEGORIAS = ['Todas', 'Writing', 'Listening', 'Reading', 'Speaking'];

export default function ClasesGrabadas({ tipo, nivel }) {
  const { usuario } = useAuth();
  const tema = getTema(tipo);
  const esProfesor = ['profesor','admin'].includes(usuario?.rol);
  const [clases, setClases] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [categoriaActiva, setCategoriaActiva] = useState('Todas');
  const [form, setForm] = useState({ titulo: '', link: '', fecha_clase: '', descripcion: '', categoria: 'Writing' });

  const cargar = async () => {
    try {
      setCargando(true);
      const res = await axios.get(`${API_URL}/clases-grabadas?tipo=${tipo}&nivel=${nivel}`);
      setClases(res.data);
    } catch {} finally { setCargando(false); }
  };

  useEffect(() => { cargar(); }, [tipo, nivel]);

  const handleGuardar = async (e) => {
    e.preventDefault();
    try {
      if (editando) {
        await axios.put(`${API_URL}/clases-grabadas/${editando}`, form);
      } else {
        await axios.post(`${API_URL}/clases-grabadas`, { ...form, tipo, nivel });
      }
      setForm({ titulo: '', link: '', fecha_clase: '', descripcion: '', categoria: 'Writing' });
      setMostrarForm(false);
      setEditando(null);
      cargar();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const iniciarEdicion = (c) => {
    setForm({ titulo: c.titulo, link: c.link, fecha_clase: c.fecha_clase, descripcion: c.descripcion || '', categoria: c.categoria || 'Writing' });
    setEditando(c.id);
    setMostrarForm(true);
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta clase grabada?')) return;
    await axios.delete(`${API_URL}/clases-grabadas/${id}`);
    cargar();
  };

  const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: '8px', background: tema.bg, border: `1px solid ${tema.border}`, color: tema.text, fontSize: '14px' };

  const clasesFiltradas = categoriaActiva === 'Todas' ? clases : clases.filter(c => c.categoria === categoriaActiva);

  const gruposPorMes = clasesFiltradas.reduce((acc, c) => {
    const fecha = new Date(c.fecha_clase);
    const clave = `${fecha.getFullYear()}-${fecha.getMonth()}`;
    const nombre = fecha.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    if (!acc[clave]) acc[clave] = { nombre, clases: [] };
    acc[clave].clases.push(c);
    return acc;
  }, {});

  return (
    <div style={{ minHeight: '100vh', background: tema.bg }}>
      <Navbar tema={tema} titulo={tipo === 'militar' ? 'Programa Militar' : 'Inglés Civil'} tipo={tipo} nivel={nivel} />
      <div className="page-layout">
        <SidebarSeccion tema={tema} tipo={tipo} nivel={nivel} />
        <main className="page-content">
          <div className="fade-in">
            <div className="section-header">
              <div>
                <h2 style={{ color: tema.text, fontSize: '26px', marginBottom: '4px' }}>🎬 Clases Grabadas</h2>
                <p style={{ color: tema.muted, fontSize: '14px' }}>Revisa las clases anteriores de {nivel}</p>
              </div>
              {esProfesor && (
                <button
                  onClick={() => {
                    setMostrarForm(!mostrarForm);
                    setEditando(null);
                    setForm({ titulo: '', link: '', fecha_clase: '', descripcion: '', categoria: categoriaActiva === 'Todas' ? 'Writing' : categoriaActiva });
                  }}
                  style={{ background: tema.primary, border: 'none', color: '#fff', padding: '10px 20px', borderRadius: '50px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(139,111,212,0.4)' }}>
                  {mostrarForm && !editando ? '✕ Cancelar' : '+ Añadir grabación'}
                </button>
              )}
            </div>

            {/* Tabs de categoría */}
            <div className="tabs-row" style={{ borderBottom: `1px solid ${tema.border}` }}>
              {CATEGORIAS.map(cat => (
                <button
                  key={cat}
                  onClick={() => setCategoriaActiva(cat)}
                  style={{
                    background: 'none', border: 'none',
                    borderBottom: categoriaActiva === cat ? `2px solid ${tema.accent}` : '2px solid transparent',
                    color: categoriaActiva === cat ? tema.accent : tema.muted,
                    padding: '10px 18px', fontSize: '14px', fontWeight: categoriaActiva === cat ? 700 : 400,
                    cursor: 'pointer', transition: 'all 0.15s',
                    marginBottom: '-1px',
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>

            {esProfesor && mostrarForm && (
              <form onSubmit={handleGuardar} style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '12px', padding: '24px', marginBottom: '28px' }}>
                <h3 style={{ color: tema.accent, marginBottom: '16px', fontSize: '16px', fontFamily: 'Inter, sans-serif' }}>
                  {editando ? 'Editar grabación' : 'Nueva grabación'}
                </h3>
                <div className="form-grid-2">
                  <div>
                    <label style={{ color: tema.muted, fontSize: '12px', display: 'block', marginBottom: '5px' }}>Título *</label>
                    <input style={inputStyle} value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} required placeholder="Ej: Clase del 15 de enero" />
                  </div>
                  <div>
                    <label style={{ color: tema.muted, fontSize: '12px', display: 'block', marginBottom: '5px' }}>Fecha de la clase *</label>
                    <input type="date" style={inputStyle} value={form.fecha_clase} onChange={e => setForm({ ...form, fecha_clase: e.target.value })} required />
                  </div>
                </div>
                <div className="form-grid-2">
                  <div>
                    <label style={{ color: tema.muted, fontSize: '12px', display: 'block', marginBottom: '5px' }}>Enlace al vídeo *</label>
                    <input style={inputStyle} value={form.link} onChange={e => setForm({ ...form, link: e.target.value })} required placeholder="https://youtube.com/watch?v=..." />
                  </div>
                  <div>
                    <label style={{ color: tema.muted, fontSize: '12px', display: 'block', marginBottom: '5px' }}>Categoría *</label>
                    <select style={inputStyle} value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })}>
                      {CATEGORIAS.filter(c => c !== 'Todas').map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ color: tema.muted, fontSize: '12px', display: 'block', marginBottom: '5px' }}>Descripción (opcional)</label>
                  <input style={inputStyle} value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} placeholder="Temas tratados en esta clase..." />
                </div>
                <button type="submit" style={{ background: tema.primary, border: 'none', color: '#fff', padding: '10px 24px', borderRadius: '50px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(139,111,212,0.4)' }}>
                  {editando ? 'Guardar cambios' : 'Añadir grabación'}
                </button>
              </form>
            )}

            {cargando ? (
              <div style={{ color: tema.muted, padding: '48px', textAlign: 'center' }}>Cargando...</div>
            ) : clasesFiltradas.length === 0 ? (
              <div style={{ color: tema.muted, textAlign: 'center', padding: '64px', background: tema.bgCard, borderRadius: '12px' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎬</div>
                <p>No hay clases grabadas{categoriaActiva !== 'Todas' ? ` de ${categoriaActiva}` : ''} disponibles aún</p>
              </div>
            ) : (
              Object.entries(gruposPorMes).map(([key, grupo]) => (
                <div key={key} style={{ marginBottom: '28px' }}>
                  <h3 style={{ color: tema.accent, fontSize: '14px', textTransform: 'capitalize', marginBottom: '12px', fontFamily: 'Inter, sans-serif', letterSpacing: '0.5px' }}>
                    📅 {grupo.nombre}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {grupo.clases.map(c => (
                      <div key={c.id} style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '12px', padding: '16px 20px' }}>
                        <div className="card-row">
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                            <span style={{ color: tema.accent, fontSize: '12px', fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
                              {new Date(c.fecha_clase).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' })}
                            </span>
                            {c.categoria && (
                              <span style={{ background: `${tema.accent}22`, color: tema.accent, borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 }}>
                                {c.categoria}
                              </span>
                            )}
                          </div>
                          <p style={{ color: tema.text, fontFamily: 'Inter, sans-serif', fontWeight: 600, marginBottom: '2px' }}>{c.titulo}</p>
                          {c.descripcion && <p style={{ color: tema.muted, fontSize: '13px' }}>{c.descripcion}</p>}
                        </div>
                        <div className="card-actions">
                          <a href={c.link} target="_blank" rel="noreferrer"
                            style={{ background: `${tema.accent}22`, border: `1px solid ${tema.accent}44`, color: tema.accent, padding: '8px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none' }}>
                            <PlayIcon style={{ width: 15, height: 15 }} /> Ver clase
                          </a>
                          {esProfesor && (
                            <>
                              <button onClick={() => iniciarEdicion(c)}
                                style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${tema.border}`, color: tema.muted, padding: '8px 12px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                <PencilSquareIcon style={{ width: 17, height: 17 }} />
                              </button>
                              <button onClick={() => eliminar(c.id)}
                                style={{ background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.2)', color: '#e74c3c', padding: '8px 10px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                <TrashIcon style={{ width: 17, height: 17 }} />
                              </button>
                            </>
                          )}
                        </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
